import { NOTIFICATION_TYPE, Reaction, Resource, StickerReactionPayload, StickersConfig } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { assignClessonStudentsSticker } from '@yandex-int/k-common/client/api/defs/assign-clesson-students-sticker';
import { cancelReactions } from '@yandex-int/k-common/client/api/defs/cancel-reactions';
import {
    createNotification,
    CreateNotificationRequest,
} from '@yandex-int/k-common/client/api/defs/create-notification';
import { getClessonStudentsStickers } from '@yandex-int/k-common/client/api/defs/get-clesson-students-stickers';
import { GetReactionsResponse } from '@yandex-int/k-common/client/api/defs/get-reactions';
import { getResourceList } from '@yandex-int/k-common/client/api/defs/get-resource-list';
// @ts-ignore
import { getStickersConfig } from 'platform-components/src/components/app-factory/selectors';
import { call, put, takeLatest, all, takeEvery, select } from 'redux-saga/effects';
import { monotonicFactory } from 'ulid';

import { getStickerResources, getStickerResourcesLoadingStatus } from 'store/stickers/selectors';

import { stickersActions } from './actions';
import { Sticker } from './typings';

const ulid = monotonicFactory();

export function* stickersSagasWatcher() {
    yield all([
        takeLatest(stickersActions.getClessonMetaStickersRequest, getClessonMetaStickersSaga),
        takeLatest(stickersActions.getClessonStudentsStickersRequest, getClessonStudentsStickersSaga),
        takeEvery(stickersActions.assignClessonStudentsSticker, assignClessonStudentsStickerSaga),
        takeEvery(stickersActions.cancelClessonStudentsSticker, cancelClessonStudentsStickerSaga),
    ]);
}

function* getClessonMetaStickersSaga(action: ReturnType<typeof stickersActions.getClessonMetaStickersRequest>) {
    try {
        const { stickerSlugs } = action.payload;
        yield loadStickerResources(stickerSlugs);

        const stickersConfig: StickersConfig = yield select(getStickersConfig) || [];
        const clessonMetaStickersConfig = stickerSlugs
            .map((slug) => stickersConfig.find((item) => item.slug === slug))
            .filter(Boolean) as StickersConfig;
        const stickerResources: Record<number, Resource | null> = yield select(getStickerResources);
        const stickers: Sticker[] = clessonMetaStickersConfig
            .map((s) => ({
                slug: s.slug,
                resource: stickerResources[s.resource_id],
            }))
            .filter((s) => Boolean(s.resource && s.resource.file))
            .map((s: { slug: string; resource: Resource }) => ({
                slug: s.slug,
                url: s.resource.file!,
            }));

        yield put(stickersActions.getClessonMetaStickersSuccess({ stickers }));
    } catch (error) {
        console.error(error);
    }
}

function* getClessonStudentsStickersSaga(action: ReturnType<typeof stickersActions.getClessonStudentsStickersRequest>) {
    try {
        const { clessonId } = action.payload;
        const args = {
            clessonId,
        };
        const { result: reactions }: GetReactionsResponse<StickerReactionPayload> = yield call(
            getClessonStudentsStickers,
            args
        );

        const reactionResourceSlugsMap = reactions.reduce(
            (result, reaction) => {
                result[reaction.payload.slug] = 1;
                return result;
            },
            {} as Record<string, 1>
        );
        const stickerSlugs = Object.keys(reactionResourceSlugsMap);

        yield loadStickerResources(stickerSlugs);

        yield put(stickersActions.getClessonStudentsStickersSuccess({ reactions }));
    } catch (error) {
        console.error(error);
    }
}

function* assignClessonStudentsStickerSaga(action: ReturnType<typeof stickersActions.assignClessonStudentsSticker>) {
    try {
        const { clessonId, courseId, slug, studentIds } = action.payload;
        const request = studentIds.map((studentId) => {
            return {
                id: ulid(),
                studentId,
                clessonId,
                courseId,
                slug,
            };
        });
        const reactions: Reaction<StickerReactionPayload>[] = yield call(assignClessonStudentsSticker, request);

        yield put(stickersActions.assignClessonStudentsStickerSuccess({ reactions }));

        yield call(createNotification, {
            target_users_ids: studentIds,
            type_slug: NOTIFICATION_TYPE.STUDENT_NEW_STICKERS_AVAILABLE,
            close_duplicates: true,
        } as CreateNotificationRequest);
    } catch (error) {
        console.error(error);
    }
}

function* cancelClessonStudentsStickerSaga(action: ReturnType<typeof stickersActions.cancelClessonStudentsSticker>) {
    try {
        yield call(cancelReactions, action.payload);
    } catch (error) {
        console.error(error);
    }
}

function* loadStickerResources(stickerSlugs: string[]) {
    const stickersConfig: StickersConfig = yield select(getStickersConfig) || [];
    const stickerConfigFiltered = stickersConfig.filter((item) => stickerSlugs.includes(item.slug));
    const resourceIds = stickerConfigFiltered.map((s) => s.resource_id);

    const loadedResources: Record<number, Resource | null> = yield select(getStickerResources);
    const loadingStatuses: Record<number, LOADING_STATUSES | null> = yield select(getStickerResourcesLoadingStatus);
    const shouldLoadResourceIds = resourceIds.filter((id) => {
        const hasResource = Boolean(loadedResources[id]);
        if (hasResource) {
            return false;
        }
        const status = loadingStatuses[id];
        return !status || (status !== LOADING_STATUSES.SUCCESS && status !== LOADING_STATUSES.LOADING);
    });

    if (shouldLoadResourceIds.length) {
        yield put(stickersActions.getStickerResourcesRequest({ resourceIds: shouldLoadResourceIds }));
        const resources: Resource[] = yield call(getResourceList, shouldLoadResourceIds);
        yield put(stickersActions.getStickerResourcesRequestSuccess({ resources }));
    }
}
